import React, { useMemo, useRef, useState } from "react";
import { dateTime, searchableDate } from "@/lib/dates";
import { Search, Check, FileText, Image as ImageIcon, FileAudio, FileVideo, File } from "lucide-react";

// Choosing one file out of ninety-three, seven of which are called snip1.png.
//
// A native <select> cannot do this: an <option> holds text and nothing else, so
// seven identical names render as seven identical rows and the choice is a coin
// toss. Everything needed to tell them apart is already in the response — size,
// date, who uploaded it — and was being thrown away.

const ICONS = { pdf: FileText, docx: FileText, text: FileText, image: ImageIcon,
                audio: FileAudio, video: FileVideo };

export function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return dateTime(d);
}

/** Name, uploader, and anything else worth searching by. */
export function matches(file, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  // Every Romanian spelling of the date, not only the one on the row: somebody
  // looking for July types "iulie", and the row says "iul.". Indexing only what
  // is rendered makes the search a quiz about which abbreviation was chosen.
  const hay = [file.name, file.uploaded_by_name, file.media_label, searchableDate(file.created_at)]
    .filter(Boolean).join(" ").toLowerCase();
  return q.split(/\s+/).every((word) => hay.includes(word));
}

export default function FilePicker({ files = [], value, onChange, emptyLabel = "Niciun fișier încărcat." }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const shown = useMemo(() => files.filter((f) => matches(f, query)), [files, query]);

  return (
    <div className="border border-line/10" data-testid="file-picker">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-line/10">
        <Search className="w-3.5 h-3.5 text-fg/40 flex-shrink-0" />
        <input
          ref={inputRef} data-testid="file-search" value={query} autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Caută în ${files.length} fișiere…`}
          aria-label="Caută un fișier"
          className="bg-transparent text-sm outline-none flex-1 placeholder-fg/30"
        />
        {query && (
          <span className="text-[11px] text-fg/40 mono" data-testid="file-count">{shown.length}</span>
        )}
      </div>

      <ul className="max-h-56 overflow-y-auto" role="listbox" aria-label="Fișiere">
        {files.length === 0 && <li className="px-3 py-3 text-xs text-fg-muted">{emptyLabel}</li>}
        {files.length > 0 && shown.length === 0 && (
          <li className="px-3 py-3 text-xs text-fg-muted" data-testid="file-none">
            Niciun fișier nu se potrivește cu „{query}".
          </li>
        )}
        {shown.map((f) => {
          const Icon = ICONS[f.media_kind] || File;
          const selected = value === f.id;
          return (
            <li key={f.id} role="option" aria-selected={selected}>
              <button
                type="button" data-testid={`file-option-${f.id}`} disabled={!f.readable}
                onClick={() => onChange?.(f.id)}
                title={f.readable ? undefined : f.unreadable_reason}
                className={`w-full text-left px-2 py-1.5 flex items-center gap-2 border-b border-line/5
                  ${selected ? "bg-brand/10" : "hover:bg-fg/5"}
                  ${f.readable ? "" : "opacity-45 cursor-not-allowed"}`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0 text-fg/40" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{f.name}</span>
                  {/* The line that makes seven identical names distinguishable.
                      Every field here is already in the response. */}
                  <span className="block text-[11px] text-fg/45 truncate" data-testid={`file-meta-${f.id}`}>
                    {[formatWhen(f.created_at), formatSize(f.size), f.uploaded_by_name]
                      .filter(Boolean).join(" · ")}
                    {f.readable ? "" : ` · ${f.unreadable_reason}`}
                  </span>
                </span>
                {selected && <Check className="w-3.5 h-3.5 text-brand flex-shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
